import { useMemo } from 'react';
import { ActorLogicFrom, ActorRefFrom } from 'xstate';
import { useFetch } from '../../../../lib/hooks/apiFetch';
import { useMerchantID } from '../../../../components/XPropsContext';
import { paymentsMachine } from './paymentsMachine';
import {
  handleFetchPaymentMethodDone,
  handleFetchPaymentMethodsDone,
  handleCreatePaymentMethodDone,
  createLogError,
  handleServiceError,
  createLogEvaluatedErrorResponse,
} from './actions';
import {
  createCreatePaymentMethod,
  createFetchPaymentMethod,
  createFetchPaymentMethods,
  evaluateErrorResponse,
} from './actors';
import {
  assignPaymentMethodResult,
  assignSelectedPaymentMethod,
  assignPaymentMethodsResult,
} from './assigns';
import {
  isPaymentsErrorResponse,
  isInvalidPaymentMethodError,
  isWriteCardAccountError,
  isGetCardConnectionError,
  isTransactionError,
  isGetPaymentMethodError,
  isAuthorizePaymentError,
  isCardTypeMismatchError,
} from './guards';

export type PaymentsMachineActorRef = ActorRefFrom<typeof paymentsMachine>;

export type PaymentsMachineActorRefLogic = ActorLogicFrom<
  typeof paymentsMachine
>;

/**
 * Provides implementation logic for payments machine
 * @returns PaymentsMachineActorRefLogic
 */
export const usePaymentsMachineLogic = (): PaymentsMachineActorRefLogic => {
  const apiFetch = useFetch();
  const { merchantID } = useMerchantID();

  const paymentsMachineLogic = useMemo(() => {
    return paymentsMachine.provide({
      guards: {
        isPaymentsErrorResponse,
        isInvalidPaymentMethodError,
        isWriteCardAccountError,
        isGetCardConnectionError,
        isTransactionError,
        isGetPaymentMethodError,
        isAuthorizePaymentError,
        isCardTypeMismatchError,
      },
      actions: {
        assignPaymentMethodResult,
        assignSelectedPaymentMethod,
        assignPaymentMethodsResult,
        handleFetchPaymentMethodDone,
        handleFetchPaymentMethodsDone,
        handleCreatePaymentMethodDone,
        handleServiceError,
        logError: createLogError({ merchantID }),
        logEvaluatedErrorResponse: createLogEvaluatedErrorResponse({
          merchantID,
        }),
      },
      actors: {
        fetchPaymentMethod: createFetchPaymentMethod({
          apiFetch,
        }),
        fetchPaymentMethods: createFetchPaymentMethods({
          apiFetch,
        }),
        createPaymentMethod: createCreatePaymentMethod({
          apiFetch,
        }),
        evaluateErrorResponse,
      },
    });
  }, []);

  return paymentsMachineLogic;
};
